import { Drawer } from '@mui/material';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getOrganizationByIdThunk } from '@redux/organization/organizationThunks';
import { updateEngagementPlansListAction } from '@redux/tasks/tasksActions';
import { getEngagementPlansThunk, scheduleEngagementPlanThunk } from '@redux/tasks/tasksThunks';
import { EngagementPlan, EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { useAlert } from 'components/AlertInfoGlobal/useAlert';
import { BackIconButton } from 'components/Button/BackButton/BackIconButton';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { TaskDetailsArea } from 'components/EngagementPlanSelectionDrawer/components/TaskDetailsArea/TaskDetailsArea';
import { StartPlanModal } from 'containers/PatientDashboard/components/StartPlanModal';
import DeletePlanModal from './components/DeletePlanModal/DeletePlanModal';
import PlanDetailsArea from './components/PlanDetailsArea/PlanDetailsArea';
import PlansListSection from './components/PlansListSection/PlansListSection';
import style from './EngagementPlanSelectionDrawer.scss';

type EngagementPlanDetailsDrawerProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  patientId: string;
  onRefreshData?: () => void;
};

type Props = ConnectedProps<typeof connectRedux> & EngagementPlanDetailsDrawerProps;

const EngagementPlanSelectionDrawer: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  patientId,
  getOrganizationById,
  getEngagementPlans,
  scheduleEngagementPlan,
  onRefreshData,
  updateEngagementPlansList
}) => {
  const [showStartPlanModal, setShowStartPlanModal] = useState(false);
  const [planToStart, setPlanToStart] = useState<EngagementPlan | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<EngagementPlan | null>(null);
  const [disabledPlan, setDisabledPlan] = useState<EngagementPlan | null>(null);
  const [selectedTask, setSelectedTask] = useState<EngagementPlanTask | null>(null);

  const { showAlertInfo, showAlertWarning } = useAlert();

  useEffect(() => {
    if (open && organizationId) {
      getEngagementPlans(organizationId);
      getOrganizationById(organizationId);
    }
  }, [open, organizationId, getEngagementPlans]);

  const onCloseDrawer = () => {
    onClose();
    setSelectedPlan(null);
    setPlanToStart(null);
    setSelectedTask(null);
    setShowStartPlanModal(false);
  };

  const onConfirmStartPlan = async (startDate: Date) => {
    if (!planToStart || !patientId) return;

    try {
      await scheduleEngagementPlan(planToStart.id, patientId, startDate);
      showAlertInfo({
        message: 'Engagement plan was successfully started!'
      });
      onRefreshData?.();
      onCloseDrawer();
    } catch (error) {
      showAlertWarning({
        message: 'Failed to start engagement plan. Please try again.'
      });
      throw error;
    }
  };

  const onClickStartPlan = (plan: EngagementPlan) => {
    setPlanToStart(plan);
    setShowStartPlanModal(true);
  };

  const onDeleteSuccess = (plan: EngagementPlan) => {
    updateEngagementPlansList({ plan, add: false });
    setSelectedPlan(null);
  };

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onCloseDrawer} variant="temporary">
        <div className={style.drawerContainer}>
          <PlansListSection
            containerStyle={style.leftSection}
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
            disablePlan={setDisabledPlan}
            organizationId={organizationId}
          />

          <div className={style.rightSection}>
            <div className={style.drawerHeader}>
              <CloseIconButton containerClassName={style.closeButton} onClick={onCloseDrawer} />
            </div>
            <PlanDetailsArea plan={selectedPlan} setSelectedTask={setSelectedTask} />
            {!!selectedPlan && (
              <div className={style.drawerFooter}>
                <Button variant="contained" onClick={() => onClickStartPlan(selectedPlan!)} className={style.continueButton}>
                  Start plan
                </Button>
              </div>
            )}
          </div>

          {selectedTask && (
            <div className={classNames(style.rightSection, style.overlaySection)}>
              <div className={classNames(style.drawerHeader, style.fullDrawerHeader)}>
                <BackIconButton onClick={() => setSelectedTask(null)} />
                <CloseIconButton onClick={onCloseDrawer} />
              </div>
              <TaskDetailsArea task={selectedTask} />
            </div>
          )}
        </div>
        <StartPlanModal open={showStartPlanModal} onClose={() => setShowStartPlanModal(false)} engagementPlan={planToStart} onConfirm={onConfirmStartPlan} />
      </Drawer>
      <DeletePlanModal visible={!!disabledPlan} plan={disabledPlan} onClosePress={() => setDisabledPlan(null)} onSuccess={onDeleteSuccess} />
    </>
  );
};

const connectRedux = connect((state: ReduxState) => ({}), {
  getEngagementPlans: getEngagementPlansThunk,
  scheduleEngagementPlan: scheduleEngagementPlanThunk,
  getOrganizationById: getOrganizationByIdThunk,
  updateEngagementPlansList: updateEngagementPlansListAction
});

export default compose(connectRedux)(EngagementPlanSelectionDrawer);
